import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Dialog, DialogContent, Typography } from '@mui/material';
import CmtCard from '@coremat/CmtCard';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import {
  useActiveCart,
  useCart,
  usePatchApproveCart,
  usePostCarrierRatePreview,
  usePostCheckout,
} from 'services/Data/CartService';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import DeliveryCheckoutSection from './Components/DeliveryCheckoutSection';
import OrderInfoCheckoutSection from './Components/OrderInfoCheckoutSection';
import { useHistory, useParams } from 'react-router-dom';
import BaseModal from 'Components/BaseModal';
import SimpleReactValidator from 'simple-react-validator';
import { validatorConfig } from 'services/Validators/Validation';
import useToast from 'services/Hooks/useToast';
import useForceUpdate from 'services/Hooks/useForceUpdate';
import { CartStatusEnumValue } from 'enums/CartStatusEnum';
import { InventoryItemCategoryEnumValue } from 'enums/InventoryItemCategoryEnum';
import { useSelector } from 'react-redux';
import ContactInfoCheckoutSection from './Components/ContactInfoCheckoutSection';
import { homePage, IsClub, IsImpersonationMode, ShowPODShippingRates } from 'config/config';
import StorePageHeader from 'Components/StorePageHeader';
import DistrictSalesManagerSection from './Components/DistrictSalesManagerSection';
import { useBrpClubDealer } from 'services/Data/DealerServices';
import { useActiveMerchandisingProgramQuote } from 'services/Data/MerchandisingProgramQuoteService';
import { useActiveExteriorSignageQuote } from 'services/Data/ExteriorSignageQuoteService';
import {
  buildPathWithBrpClubDealerSelectionTrigger,
  shouldAutoOpenBrpClubDealerSelection,
} from 'services/Utilities/dealerWorkUtils';

const useStyles = makeStyles(() => ({
  pageFull: {
    width: '100%',
  },
  noItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cartFooter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  deliverySection: {
    position: 'sticky',
    top: '10vh',
  },
  card: {
    overflow: 'visible',
  },
}));

const carrierRatePreviewDebounceMs = 400;

const Checkout = ({ fromHistory, fromProfile }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const forceUpdate = useForceUpdate();

  const { errorMessage } = useToast();
  const validator = useRef(new SimpleReactValidator(validatorConfig.getConfig(t)));

  const [showCheckoutConfirmed, setShowCheckoutConfirmed] = useState(false);
  const [showApprouveModal, setShowApprouveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [carrierRatePreview, setCarrierRatePreview] = useState(null);
  const [carrierRatePreviewError, setCarrierRatePreviewError] = useState('');
  const [selectedFreightPodServiceCode, setSelectedFreightPodServiceCode] = useState('');
  const carrierRatePreviewRequestId = useRef(0);

  const accountReducer = useSelector(state => state.AccountReducer);
  const dealerId = accountReducer?.profile?.dealer_Id;
  const activeDealerId = accountReducer?.profile?.temporaryDealer_Id;
  const { data: clubDealer } = useBrpClubDealer(activeDealerId);

  let { cartId } = useParams();

  // active cart is used in cart (it's the current cart being in use)
  // history cart is used in the history only
  const { data: activeCart, isLoading: gettingCart } = useActiveCart(
    accountReducer.isLoggedIn,
    true,
    IsImpersonationMode ? dealerId : null,
  );
  const { data: historyCart, isLoading: gettingCartHistory } = useCart(cartId);
  const {
    data: activeMerchandisingProgramQuote,
    isLoading: gettingActiveMerchandisingProgramQuote,
  } = useActiveMerchandisingProgramQuote(accountReducer.isLoggedIn);
  const { data: activeExteriorSignageQuote, isLoading: gettingActiveExteriorSignageQuote } = useActiveExteriorSignageQuote(
    accountReducer.isLoggedIn,
  );

  const cart = useMemo(() => (cartId ? historyCart : activeCart), [activeCart, historyCart, cartId]);

  const { mutate: checkout, isPending: isCheckingOut } = usePostCheckout();
  const { mutateAsync: changeStatut } = usePatchApproveCart();
  const { mutateAsync: previewCarrierRates, isPending: isLoadingCarrierRatePreview } = usePostCarrierRatePreview();

  const backUrl = useMemo(() => (fromHistory ? '/dealer/profile/request-histories' : '/dealer/profile/awaiting-requests'), [
    fromHistory,
  ]);
  const shouldAutoOpenDealerSelection = useMemo(
    () =>
      shouldAutoOpenBrpClubDealerSelection({
        isClub: IsClub,
        isLoading: gettingCart || gettingActiveMerchandisingProgramQuote || gettingActiveExteriorSignageQuote,
        cart: activeCart,
        merchandisingProgramQuote: activeMerchandisingProgramQuote,
        exteriorSignageQuote: activeExteriorSignageQuote,
      }),
    [
      activeCart,
      activeExteriorSignageQuote,
      activeMerchandisingProgramQuote,
      gettingActiveExteriorSignageQuote,
      gettingActiveMerchandisingProgramQuote,
      gettingCart,
    ],
  );
  const confirmedCheckoutRedirectPath = useMemo(
    () => (shouldAutoOpenDealerSelection ? buildPathWithBrpClubDealerSelectionTrigger(homePage) : homePage),
    [shouldAutoOpenDealerSelection],
  );

  const selectedCarrierRateOption = useMemo(() => {
    if (!carrierRatePreview?.rateOptions?.length || !selectedFreightPodServiceCode) {
      return null;
    }

    return (
      carrierRatePreview.rateOptions.find(rateOption => rateOption.serviceCode === selectedFreightPodServiceCode) || null
    );
  }, [carrierRatePreview, selectedFreightPodServiceCode]);

  const isPrintOnDemandOnlyCart = useMemo(() => {
    if (!cart?.cartItems?.length) {
      return false;
    }

    if (cart?.cartWalls?.length || cart?.cartToolings?.length) {
      return false;
    }

    return cart.cartItems.every(
      item =>
        item?.inventoryItemCategoryEnum === InventoryItemCategoryEnumValue.PrintOnDemand &&
        (!item?.bundledItems?.length ||
          item.bundledItems.every(
            bundledItem => bundledItem?.inventoryItemCategoryEnum === InventoryItemCategoryEnumValue.PrintOnDemand,
          )),
    );
  }, [cart]);

  const shouldShowPodShippingRates = useMemo(() => ShowPODShippingRates && isPrintOnDemandOnlyCart && !fromProfile, [
    fromProfile,
    isPrintOnDemandOnlyCart,
  ]);

  const requiresFreightPodSelection = useMemo(
    () =>
      shouldShowPodShippingRates &&
      carrierRatePreview?.isEnabled !== false &&
      !!carrierRatePreview?.shouldRequestRates &&
      !!carrierRatePreview?.rateOptions?.length &&
      !carrierRatePreviewError,
    [carrierRatePreview, carrierRatePreviewError, shouldShowPodShippingRates],
  );

  const isMissingRequiredFreightPodSelection = useMemo(
    () => !cartId && requiresFreightPodSelection && !selectedFreightPodServiceCode,
    [cartId, requiresFreightPodSelection, selectedFreightPodServiceCode],
  );
  const hasDepletedInventoriedItems = useMemo(
    () => cart?.cartItems?.some(item => item?.isInventoriedItemDepleted) ?? false,
    [cart?.cartItems],
  );

  const handleCheckout = useCallback(() => {
    if (hasDepletedInventoriedItems) {
      errorMessage({
        message: 'inventoriedItemDepletedRequiresReplacement',
      });
      return;
    }

    if (!validator.current.allValid()) {
      validator.current.showMessages();
      forceUpdate();

      errorMessage({
        message: 'contraint_errors',
      });
      return;
    }

    if (requiresFreightPodSelection && !selectedFreightPodServiceCode) {
      errorMessage({
        message: 'checkoutCarrierRates.selectionRequired',
      });
      return;
    }

    validator.current.hideMessages();

    checkout(
      {
        cartId: cart.id,
        selectedFreightPodServiceCode,
      },
      {
        onSuccess: () => {
          setShowCheckoutConfirmed(true);
        },
      },
    );
  }, [
    cart,
    checkout,
    errorMessage,
    forceUpdate,
    hasDepletedInventoriedItems,
    requiresFreightPodSelection,
    selectedFreightPodServiceCode,
  ]);

  const checkoutActionBtns = useMemo(() => {
    if (cart?.status === CartStatusEnumValue.PendingDealerApproval || cart?.status === CartStatusEnumValue.Active) {
      return [
        {
          variant: 'contained',
          color: 'secondary',
          action:
            cartId && cart?.status === CartStatusEnumValue.PendingDealerApproval
              ? () => setShowApprouveModal(true)
              : handleCheckout,
          label:
            cartId && cart?.status === CartStatusEnumValue.PendingDealerApproval ? t('approveCart') : t('proceedCheckout'),
          disabled:
            !cart ||
            (cartId && cart?.status !== CartStatusEnumValue.PendingDealerApproval) ||
            isMissingRequiredFreightPodSelection ||
            hasDepletedInventoriedItems,
        },
        {
          variant: 'contained',
          color: 'error',
          hidden: !cartId,
          action: () => setShowCancelModal(true),
          label: t('cancel'),
        },
      ];
    }

    if (cart?.status === CartStatusEnumValue.PendingBrpApproval) {
      return [
        {
          variant: 'contained',
          color: 'error',
          hidden: !cartId,
          action: () => setShowCancelModal(true),
          label: t('cancel'),
        },
      ];
    }

    return [];
  }, [cart, cartId, handleCheckout, hasDepletedInventoriedItems, isMissingRequiredFreightPodSelection, t]);

  const handleCloseCancelCartModal = useCallback(() => {
    setShowCancelModal(false);
  }, [setShowCancelModal]);

  const handleCloseApproveCartModal = useCallback(() => {
    setShowApprouveModal(false);
  }, [setShowApprouveModal]);

  const handleCloseConfirmedCheckoutModal = useCallback(() => {
    setShowCheckoutConfirmed(false);
  }, [setShowCheckoutConfirmed]);

  const handleChangeStatus = useCallback(
    newStatus => {
      changeStatut({ ...cart, status: newStatus }).then(() => {
        history.push(backUrl);
      });
    },
    [cart, changeStatut, backUrl, history],
  );

  useEffect(() => {
    if (!shouldShowPodShippingRates) {
      setCarrierRatePreview(null);
      setCarrierRatePreviewError('');
      return;
    }

    if (
      !cart?.id ||
      !cart?.shippingAddress ||
      !cart?.shippingCity ||
      !cart?.shippingPostalCode ||
      !cart?.shippingRegion_Id
    ) {
      setCarrierRatePreview(null);
      setCarrierRatePreviewError('');
      return;
    }

    const requestId = ++carrierRatePreviewRequestId.current;
    const timeoutId = setTimeout(() => {
      previewCarrierRates({ cartId: cart.id })
        .then(preview => {
          if (requestId !== carrierRatePreviewRequestId.current) {
            return;
          }

          setCarrierRatePreview(preview);
          setCarrierRatePreviewError('');
        })
        .catch(() => {
          if (requestId !== carrierRatePreviewRequestId.current) {
            return;
          }

          setCarrierRatePreview(null);
          setCarrierRatePreviewError('checkoutCarrierRates.loadError');
        });
    }, carrierRatePreviewDebounceMs);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    cart?.id,
    cart?.shippingAddress,
    cart?.shippingCity,
    cart?.shippingPostalCode,
    cart?.shippingRegion_Id,
    previewCarrierRates,
    shouldShowPodShippingRates,
  ]);

  useEffect(() => {
    if (!carrierRatePreview?.rateOptions?.length) {
      setSelectedFreightPodServiceCode('');
      return;
    }

    const hasSelectedRate = carrierRatePreview.rateOptions.some(
      rateOption => rateOption.serviceCode === selectedFreightPodServiceCode,
    );

    if (!hasSelectedRate) {
      setSelectedFreightPodServiceCode('');
    }
  }, [carrierRatePreview, selectedFreightPodServiceCode]);

  return (
    <>
      {!fromProfile && (
        <Box sx={{ mb: 9 }}>
          <StorePageHeader title={t('checkout')} imgLeft backgroundColor="lightGray" borderRadius="0.5rem" />
        </Box>
      )}

      <Box className={classes.pageFull}>
        <Dialog
          open={isCheckingOut}
          fullWidth
          maxWidth="xs"
          onClose={() => {}}
          disableEscapeKeyDown
          aria-labelledby="checkout-progress-title"
          aria-describedby="checkout-progress-description">
          <DialogContent>
            <Box
              sx={{
                py: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}>
              <CircularProgress color="secondary" />
              <Typography id="checkout-progress-title" variant="h2" align="center">
                {t('checkoutProcessing')}
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
        <BaseModal
          show={showCheckoutConfirmed}
          title={t('checkoutConfirmed')}
          message={t('checkoutConfirmedText')}
          closeBtnMessage={t('close')}
          onClose={handleCloseConfirmedCheckoutModal}
          preventClosing={true}
          actions={[
            {
              onClick: () => history.push(confirmedCheckoutRedirectPath),
              color: 'secondary',
              style: { color: 'black', borderRadius: 0 },
              variant: 'contained',
              message: t('goToHome'),
            },
          ]}
        />
        <BaseModal
          show={showApprouveModal}
          title={t('approveCart')}
          message={t('approveCartText')}
          closeBtnMessage={t('close')}
          onClose={handleCloseApproveCartModal}
          actions={[
            {
              onClick: () => {
                handleChangeStatus(CartStatusEnumValue.Accepted);
              },
              color: 'secondary',
              style: { color: 'black', borderRadius: 0 },
              variant: 'contained',
              message: t('approve'),
            },
          ]}
        />
        <BaseModal
          show={showCancelModal}
          title={t('cancelCart')}
          message={t('cancelCartText')}
          closeBtnMessage={t('close')}
          onClose={handleCloseCancelCartModal}
          actions={[
            {
              onClick: () => {
                handleChangeStatus(CartStatusEnumValue.Cancelled);
                history.push(backUrl);
              },
              color: 'error',
              style: { color: 'white' },
              variant: 'contained',
              message: t('cancel'),
            },
          ]}
        />
        <CmtCard className={classes.card}>
          <CmtCardHeader>
            {cartId && (
              <Button sx={{ borderRadius: 0 }} variant={'contained'} color="secondary" onClick={() => history.push(backUrl)}>
                <Typography
                  sx={{
                    color: 'black !important',
                    fontSize: 12,
                    fontWeight: 700,
                    paddingRight: 10,
                    paddingLeft: 10,
                    paddingTop: 2,
                    paddingBottom: 2,
                  }}>
                  {t('backToList')}
                </Typography>
              </Button>
            )}
          </CmtCardHeader>
          <CmtCardContent>
            <GridContainer>
              <GridItem xs={12} md={6}>
                <Box className={classes.deliverySection}>
                  <Typography variant="h1" sx={{ mb: 4 }}>
                    {t('contactInformation')}
                  </Typography>
                  <Box sx={{ mb: 6 }}>
                    <ContactInfoCheckoutSection
                      cart={cart}
                      isLoading={gettingCart || gettingCartHistory || isCheckingOut}
                      validator={validator}
                      isDisabled={cart?.status !== CartStatusEnumValue.PendingDealerApproval && cartId}
                      isClubMode={IsClub}
                      clubDealer={clubDealer}
                    />
                  </Box>

                  {IsImpersonationMode && !IsClub && (
                    <Box sx={{ mb: 6 }}>
                      <DistrictSalesManagerSection
                        cart={cart}
                        isLoading={gettingCart || gettingCartHistory || isCheckingOut}
                        isDisabled={cart?.status !== CartStatusEnumValue.PendingDealerApproval && cartId}
                      />
                    </Box>
                  )}

                  <Typography variant="h1">{t('delivery')}</Typography>
                  <DeliveryCheckoutSection
                    cart={cart}
                    isLoading={gettingCart || gettingCartHistory || isCheckingOut}
                    validator={validator}
                    isDisabled={cart?.status !== CartStatusEnumValue.PendingDealerApproval && cartId}
                  />
                </Box>
              </GridItem>
              <GridItem xs={12} md={6}>
                <Box>
                  <Typography variant="h1">{t('orderInfo')}</Typography>
                  <OrderInfoCheckoutSection
                    cart={cart}
                    isLoading={gettingCart || gettingCartHistory || isCheckingOut}
                    fromProfile={fromProfile}
                    isDisabled={cartId}
                    hasDepletedInventoriedItems={hasDepletedInventoriedItems}
                    carrierRatePreview={carrierRatePreview}
                    carrierRatePreviewError={carrierRatePreviewError}
                    isLoadingCarrierRatePreview={isLoadingCarrierRatePreview}
                    shouldShowPodShippingRates={shouldShowPodShippingRates}
                    selectedFreightPodServiceCode={selectedFreightPodServiceCode}
                    selectedCarrierRateOption={selectedCarrierRateOption}
                    onSelectedFreightPodServiceCodeChange={setSelectedFreightPodServiceCode}
                    actionBtns={checkoutActionBtns}
                  />
                </Box>
              </GridItem>
            </GridContainer>
          </CmtCardContent>
        </CmtCard>
      </Box>
    </>
  );
};

export default Checkout;
